import {
    CustomPaging,
    PagingState,
    SortingState,
    TableColumnResizing,
} from '@devexpress/dx-react-grid';
import {
    DragDropProvider,
    Grid,
    PagingPanel,
    Table,
    TableColumnReordering,
    TableHeaderRow,
} from '@devexpress/dx-react-grid-material-ui';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import { LinearProgress } from 'material-ui';
import CheckIcon from 'material-ui-icons/Check';
import Paper from 'material-ui/Paper';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Link from 'react-router-dom/Link';
import { reduxForm } from 'redux-form';
import getUsersList from '../../../actions/getUsersList';
import SimpleMenu from '../../../helpers/SimpleMenu';
import SkeletonTableRows from '../../../helpers/SkeletonTableRows';
import TableFilters from '../../../helpers/TableFilters';
import { cacheTableOptions, getTableOptions, isUserATSysAdmin } from '../../../helpers/Util';
import Layout from '../../Layout';
import { ProductMap } from '../../../helpers/ProductMap';
import { isSysAdmin } from '../../../helpers/security/roles';
import OverflowTip from '../../../helpers/OverflowTip';
import {
    Clear as InactiveIcon
} from '@material-ui/icons';

const componentKey = 'user-list';
// Debounce server-side search so each keystroke from TableFilters doesn't fire a request.
const SEARCH_DEBOUNCE_MS = 350;
const paperStyle = {
    width: '80%',
    height: '70%',
    display: 'inline-block',
    margin: 20,
    marginTop: '4vh'
};

class UserList extends Component {
    constructor(props) {
        super(props);

        const columns = [
            { name: 'isActive', title: 'Active' },
            { name: 'email', title: 'Username / Email' },
            { name: 'firstName', title: 'First Name' },
            { name: 'lastName', title: 'Last Name' },
            { name: 'suffix', title: 'Suffix' },
            { name: 'menu', title: ' ' },
        ];
        this.skeletonRows = SkeletonTableRows({ columns, rowKey: 'personId' });

        this.state = {
            addLink: '/add-edit-user',
            columns,
            columnOrder: ['isActive', 'email', 'firstName', 'lastName', 'suffix', 'menu'],
            columnWidths: [
                { columnName: 'isActive', width: '6.67%' },
                { columnName: 'email', width: '50%' },
                { columnName: 'firstName', width: '19.33%' },
                { columnName: 'lastName', width: '19.33%' },
                { columnName: 'suffix', width: '10.67%' },
                { columnName: 'menu', width: '21.33%' },
            ],
            currentPage: 0,
            pageSize: 10,
            pageSizes: [5, 10, 25],
            sorting: [{ columnName: 'email', direction: 'asc' }],
            searchBy: 'email',
            searchTerm: '',
            product: ProductMap[sessionStorage.getItem('product')] || 'all',
            initialValues: {},
            searchByOptions: [
                { name: 'email', label: 'Username / Email' },
                { name: 'firstName', label: 'First Name' },
                { name: 'lastName', label: 'Last Name' },
                { name: 'suffix', label: 'Suffix' },
            ],
            tableColumnExtensions: [],
            title: 'Users',
            productFilterOptions: [
                { name: 'all', label: 'All' },
                { name: 'aci', label: 'AutoCheck Insurance' },
                { name: 'aca', label: 'AutoCheck Auctions' },
                { name: 'vxc', label: 'VIN Cross Check' },
            ],
        };
    }

    componentDidMount() {
        const opts = getTableOptions(componentKey);
        if (opts) {
            const { initialValues = {}, ...layout } = opts;
            this.setState({
                ...layout,
                initialValues,
                searchBy: initialValues.searchBy || 'email',
                searchTerm: initialValues.searchData || '',
                product: layout.filterByProduct || this.state.product,
            }, this.fetchUsers);
        } else {
            this.fetchUsers();
        }
    }

    componentDidUpdate(prevProps) {
        // Re-fetch when the route switches between the all-users and per-location views.
        if (prevProps.match.params.id !== this.props.match.params.id) {
            this.setState({ currentPage: 0 }, this.fetchUsers);
        }
    }

    componentWillUnmount() {
        if (this.searchDebounce) clearTimeout(this.searchDebounce);
    }

    fetchUsers = () => {
        const { currentPage, pageSize, sorting, searchTerm, searchBy, product } = this.state;
        const { match, location } = this.props;

        const params = {
            customerId: localStorage.getItem('customerId'),
            product,
            isATSysAdmin: isUserATSysAdmin(localStorage.getItem('customerId')),
            page: currentPage,
            pageSize,
            sortBy: (sorting && sorting.length)
                ? { column: sorting[0].columnName, direction: sorting[0].direction }
                : undefined,
            search: searchTerm ? { term: searchTerm, column: searchBy } : undefined,
        };

        if (location.pathname.includes('/location/') && match.params.id) {
            params.locationId = match.params.id;
        }

        this.props.actions.fetchUsers(this.props.config, params);
    };

    rowMapper = row => ({
        isActive: (row.isActive === undefined || row.isActive === null) ? true : row.isActive,
        ...row,
    });

    getRowId = row => row.personId;

    changeCurrentPage = (currentPage) => {
        cacheTableOptions(componentKey, { currentPage });
        this.setState({ currentPage }, this.fetchUsers);
    };

    changePageSize = (pageSize) => {
        cacheTableOptions(componentKey, { pageSize, currentPage: 0 });
        this.setState({ pageSize, currentPage: 0 }, this.fetchUsers);
    };

    changeColumnOrder = (columnOrder) => {
        cacheTableOptions(componentKey, { columnOrder });
        this.setState({ columnOrder });
    };

    changeColumnWidths = (columnWidths) => {
        cacheTableOptions(componentKey, { columnWidths });
        this.setState({ columnWidths });
    };

    changeSorting = (sorting) => {
        if (!sorting || !sorting.length) return;
        cacheTableOptions(componentKey, { sorting, currentPage: 0 });
        this.setState({ sorting, currentPage: 0 }, this.fetchUsers);
    };

    // TableFilters auto-submits on every keystroke; debounce before hitting the server.
    changeFilters = (filters) => {
        if (this.searchDebounce) clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => this.applyFilters(filters), SEARCH_DEBOUNCE_MS);
    };

    applyFilters = (filters) => {
        if (!filters) return;
        const { searchBy, searchData, filterByProduct } = filters;
        // Reset the page with the filters (and cache it too) so a restored visit
        // lands on page 1 of the new search, not a stale page from before it.
        cacheTableOptions(componentKey, { ...filters, currentPage: 0 });
        this.setState({
            searchBy: searchBy || 'email',
            searchTerm: searchData || '',
            product: filterByProduct || this.state.product,
            currentPage: 0,
        }, this.fetchUsers);
    };

    cell = ({ column, style, tableRow, value }) => {
        if (tableRow.row.isSkeletonRow) {
            return <Table.Cell style={style}>{value}</Table.Cell>;
        }

        let content = <OverflowTip value={value}>{value}</OverflowTip>;

        if (column.name === 'menu') {
            content = this.renderMoreMenu(tableRow);
        } else if (column.name === 'isActive') {
            const className = (value || value === undefined) ? 'active-1' : 'active-3';
            const active = (value || value === undefined);
            content = (<Tooltip title={value ? 'Active' : 'Inactive'} placement="left">
                {active ? <CheckIcon className={className} /> : <InactiveIcon className={className} />}
                       </Tooltip>);
        }

        return <Table.Cell style={style}>{content}</Table.Cell>;
    };

    renderMoreMenu = (tableRow) => {
        const { row: { personId, cognitoId } } = tableRow;

        return (
            <SimpleMenu id={personId}>
                <MenuItem
                    key="edit-user"
                    component={Link}
                    to={`/add-edit-user/${cognitoId}`}
                >
                    Edit User
                </MenuItem>
                <MenuItem
                    key="list-users-by-location"
                    component={Link}
                    to={`/locations/user/${personId}`}
                >
                    View Locations
                </MenuItem>
            </SimpleMenu>
        );
    };

    render() {
        const {
            addLink,
            columns,
            columnOrder,
            columnWidths,
            currentPage,
            initialValues,
            pageSize,
            pageSizes,
            searchByOptions,
            sorting,
            tableColumnExtensions,
            title,
            productFilterOptions,
        } = this.state;

        const { items, totalRows, fetching } = this.props.usersList;
        const rows = fetching ? this.skeletonRows : items.map(this.rowMapper);

        return (
            <Layout
                className="App"
                showMenu
                menuTitle=""
                toolTip="Having problems? Please review the information you have entered to be sure everything is correct.  If you continue to have problems please contact our Customer Service department at (205) 414-2727."
            >
                <Paper style={paperStyle}>
                    {fetching && <LinearProgress className="linear-progress-top" />}
                    <Grid
                        rows={rows}
                        columns={columns}
                        getRowId={this.getRowId}
                    >
                        <TableFilters
                            addLink={addLink}
                            changeFilters={this.changeFilters}
                            initialValues={initialValues}
                            searchByOptions={searchByOptions}
                            title={title}
                            productFilterOptions={this.props.person && isSysAdmin(this.props.person) ? productFilterOptions : []}
                            showCloseEnd
                        />

                        <SortingState
                            sorting={sorting}
                            onSortingChange={this.changeSorting}
                        />

                        <PagingState
                            currentPage={currentPage}
                            onCurrentPageChange={this.changeCurrentPage}
                            pageSize={pageSize}
                            onPageSizeChange={this.changePageSize}
                        />

                        <CustomPaging totalCount={totalRows} />

                        <DragDropProvider />

                        <Table
                            columnExtensions={tableColumnExtensions}
                            cellComponent={this.cell}
                        />

                        <TableColumnReordering
                            order={columnOrder}
                            onOrderChange={this.changeColumnOrder}
                        />

                        <TableColumnResizing
                            columnWidths={columnWidths}
                            onColumnWidthsChange={this.changeColumnWidths}
                        />

                        <TableHeaderRow
                            showSortingControls
                        />

                        <PagingPanel pageSizes={pageSizes} />
                    </Grid>
                </Paper>
            </Layout>
        );
    }
}

const reduxToProps = state => ({
    config: state.config.config,
    person: state.person.person,
    usersList: state.usersList,
});

const dispatchToProps = dispatch => ({
    actions: {
        fetchUsers: (config, params) => dispatch(getUsersList(config, params)),
    }
});

const ConnectedComponent = connect(reduxToProps, dispatchToProps)(UserList);

export default (reduxForm({ form: 'UserList' })(ConnectedComponent));



// WEBPACK FOOTER //
// ./src/components/Customer/User/UserList.js